import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, Users, Ticket, AlertTriangle } from "lucide-react";
import api, { money } from "../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "Receita hoje", value: money(stats.today_revenue || 0), icon: ShoppingCart, testid: "stat-today-revenue" },
    { label: "Pedidos hoje", value: stats.today_orders || 0, icon: ShoppingCart, testid: "stat-today-orders" },
    { label: "Receita total", value: money(stats.revenue), icon: ShoppingCart, testid: "stat-revenue" },
    { label: "Pedidos pagos", value: stats.orders_paid, icon: ShoppingCart, testid: "stat-paid" },
    { label: "Pedidos pendentes", value: stats.orders_pending, icon: ShoppingCart, testid: "stat-pending" },
    { label: "Produtos", value: stats.products, icon: Package, testid: "stat-products" },
    { label: "Clientes", value: stats.customers, icon: Users, testid: "stat-customers" },
    { label: "Cupons ativos", value: stats.coupons, icon: Ticket, testid: "stat-coupons" },
    { label: "Erros de webhook", value: stats.webhook_errors, icon: AlertTriangle, testid: "stat-webhook-errors" },
    { label: "Estoque baixo", value: (stats.low_stock || []).length, icon: Package, testid: "stat-low-stock" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl tracking-tight mb-10">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-14">
        {cards.map((c) => (
          <div key={c.testid} className="border border-white/10 rounded-lg p-6 bg-[#111111]" data-testid={c.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps">{c.label}</span>
              <c.icon size={15} className="text-gold/60" />
            </div>
            <p className="font-display text-3xl text-zinc-100">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
        <div className="border border-white/10 rounded-lg p-6 bg-[#111111]" data-testid="dashboard-low-stock">
          <p className="label-caps mb-4 flex items-center gap-2"><Package size={13} /> Estoque baixo (≤ 3)</p>
          {(stats?.low_stock || []).length === 0 ? (
            <p className="text-sm text-zinc-600">Nenhuma variação com estoque baixo.</p>
          ) : (
            <div className="space-y-2.5">
              {stats.low_stock.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-zinc-300">{l.product} <span className="text-zinc-600">({l.variation} · {l.sku})</span></span>
                  <span className={`font-semibold ${l.stock === 0 ? "text-red-400" : "text-amber-400"}`}>{l.stock} un</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-white/10 rounded-lg p-6 bg-[#111111]" data-testid="dashboard-top-products">
          <p className="label-caps mb-4 flex items-center gap-2"><ShoppingCart size={13} /> Mais vendidos</p>
          {(stats?.top_products || []).length === 0 ? (
            <p className="text-sm text-zinc-600">Nenhuma venda confirmada ainda.</p>
          ) : (
            <div className="space-y-2.5">
              {stats.top_products.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-zinc-300"><span className="text-gold font-mono mr-2">{i + 1}º</span>{t.name}</span>
                  <span className="text-zinc-400">{t.qty} vendidos</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl tracking-tight">Pedidos recentes</h2>
        <Link to="/admin/pedidos" className="text-sm text-gold hover:underline" data-testid="dashboard-all-orders">Ver todos</Link>
      </div>
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="dashboard-orders-table">
          <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Pedido</th>
              <th className="text-left px-5 py-3">Cliente</th>
              <th className="text-left px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-600">Nenhum pedido ainda.</td></tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3.5">{o.order_number ? `#${String(o.order_number).padStart(4, "0")}` : "—"}</td>
                <td className="px-5 py-3.5 text-zinc-300">{o.customer?.name}</td>
                <td className="px-5 py-3.5 text-gold">{money(o.total)}</td>
                <td className="px-5 py-3.5">
                  <span className={`text-xs px-2 py-1 rounded ${o.status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                    {o.status === "paid" ? "Pago" : "Pendente"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
